import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "../../axiosConfig";
import { useToast } from "../commons/toast/ToastContext";
import { IoMdMale, IoMdFemale } from "react-icons/io";
import { FaCamera, FaTrash } from "react-icons/fa";
import { CustomDatePicker } from '../commons/datepicker';
import "../../styles/DogInfoTab.css";

const DogInfoTab = ({ dog, owner, canEdit, onDogUpdated, onDogDeleted }) => {
    const { t, i18n } = useTranslation(['dogs', 'common']);
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [currentPhotoUrl, setCurrentPhotoUrl] = useState(dog?.photoUrl || null);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const photoInputRef = useRef(null);
    const [editForm, setEditForm] = useState({
        id: "",
        name: "",
        breed: "",
        dateOfBirth: "",
        approximate: false,
        gender: "",
    });
    const toast = useToast();

    const formatGender = (gender) => {
        if (gender === "MALE") return t('dogs:male');
        if (gender === "FEMALE") return t('dogs:female');
        return gender || t('dogs:genderPlaceholder');
    };

    const formatAddress = (address) => {
        if (!address) return t('dogs:noData');
        return `${address.street} ${address.buildingNumber}${address.apartmentNumber ? "/" + address.apartmentNumber : ""}, ${address.postalCode} ${address.city}, ${address.country}`;
    };

    const startEditing = () => {
        setEditForm({
            id: dog.id,
            name: dog.name || "",
            breed: dog.breed || "",
            dateOfBirth: dog.dateOfBirth || "",
            approximate: dog.approximate || false,
            gender: dog.gender || "",
        });
        setIsEditing(true);
    };

    const cancelEditing = () => {
        setIsEditing(false);
        setEditForm({ id: "", name: "", breed: "", dateOfBirth: "", approximate: false, gender: "" });
    };

    const handleEditChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditForm((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleSave = async () => {
        if (!editForm.name.trim() || !editForm.breed.trim() || !editForm.dateOfBirth || !editForm.gender) {
            toast.error(t('dogs:allFieldsRequired'));
            return;
        }
        try {
            setSaving(true);
            await axios.put("/api/v1/dog/trainer/edit", editForm);
            toast.success(t('dogs:pupilUpdatedSuccess'));
            setIsEditing(false);
            if (onDogUpdated) onDogUpdated();
        } catch (err) {
            console.error("Error updating dog:", err);
            if (err.response?.data?.message) {
                toast.error(err.response.data.message);
            } else {
                toast.error(t('dogs:pupilUpdateError'));
            }
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        try {
            setDeleting(true);
            await axios.delete(`/api/v1/dog/trainer/delete/${dog.id}`);
            toast.success(t('dogs:pupilDeletedSuccess'));
            if (onDogDeleted) onDogDeleted();
        } catch (err) {
            console.error("Error deleting dog:", err);
            if (err.response?.data?.message) {
                toast.error(err.response.data.message);
            } else {
                toast.error(t('dogs:pupilDeleteError'));
            }
        } finally {
            setDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    const handlePhotoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
        if (!allowedTypes.includes(file.type)) {
            toast.error(t('dogs:photoAllowedFormats'));
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            toast.error(t('dogs:photoMaxSize'));
            return;
        }

        const formData = new FormData();
        formData.append("file", file);

        try {
            setUploadingPhoto(true);
            const response = await axios.post(`/api/v1/dog/${dog.id}/photo`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setCurrentPhotoUrl(response.data.photoUrl);
            if (onDogUpdated) onDogUpdated();
        } catch (error) {
            console.error("Error uploading photo:", error);
            toast.error(t('dogs:photoUploadError'));
        } finally {
            setUploadingPhoto(false);
            if (photoInputRef.current) photoInputRef.current.value = "";
        }
    };

    const handlePhotoDelete = async () => {
        try {
            setUploadingPhoto(true);
            await axios.delete(`/api/v1/dog/${dog.id}/photo`);
            setCurrentPhotoUrl(null);
            if (onDogUpdated) onDogUpdated();
        } catch (error) {
            console.error("Error deleting photo:", error);
            toast.error(t('dogs:photoDeleteError'));
        } finally {
            setUploadingPhoto(false);
        }
    };

    if (!dog) {
        return <div className="dog-info-tab__loading">{t('dogs:loadingDogData')}</div>;
    }

    return (
        <div className="dog-info-tab">
            {/* Dog Information Section */}
            <div className="dog-info-tab__section">
                <div className="dog-info-tab__section-header">
                    <h3 className="dog-info-tab__section-title">{t('dogs:animalInfo')}</h3>
                    {canEdit && !isEditing && (
                        <button className="dog-info-tab__edit-btn" onClick={startEditing}>
                            {t('common:edit')}
                        </button>
                    )}
                </div>

                {/* Photo + Fields side by side */}
                <div className="dog-info-tab__hero">
                    <div className="dog-info-tab__photo-side">
                        <div className="dog-info-tab__photo-container">
                            {currentPhotoUrl ? (
                                <img
                                    src={currentPhotoUrl}
                                    alt={dog.name || t('dogs:pupilPhoto')}
                                    className="dog-info-tab__photo"
                                />
                            ) : (
                                <div className="dog-info-tab__photo-placeholder">
                                    <img src="/paw512.png" alt="Placeholder" className="dog-info-tab__paw-icon" />
                                </div>
                            )}
                        </div>
                        {canEdit && (
                            <div className="dog-info-tab__photo-actions">
                                <button
                                    className="dog-info-tab__photo-btn"
                                    onClick={() => photoInputRef.current?.click()}
                                    disabled={uploadingPhoto}
                                    type="button"
                                >
                                    <FaCamera size={12} />
                                    {currentPhotoUrl ? t('dogs:photoChange') : t('dogs:photoAdd')}
                                </button>
                                {currentPhotoUrl && (
                                    <button
                                        className="dog-info-tab__photo-btn dog-info-tab__photo-btn--delete"
                                        onClick={handlePhotoDelete}
                                        disabled={uploadingPhoto}
                                        type="button"
                                    >
                                        <FaTrash size={12} />
                                        {t('dogs:photoRemove')}
                                    </button>
                                )}
                            </div>
                        )}
                        <input
                            ref={photoInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={handlePhotoUpload}
                            style={{ display: "none" }}
                        />
                    </div>

                    <div className="dog-info-tab__fields-side">
                        {isEditing ? (
                            <>
                                <div className="dog-info-tab__field-group">
                                    <label className="dog-info-tab__label">{t('dogs:name')}</label>
                                    <input className="dog-info-tab__input" type="text" name="name" value={editForm.name} onChange={handleEditChange} placeholder={t('dogs:namePlaceholder')} />
                                </div>
                                <div className="dog-info-tab__field-group">
                                    <label className="dog-info-tab__label">{t('dogs:breed')}</label>
                                    <input className="dog-info-tab__input" type="text" name="breed" value={editForm.breed} onChange={handleEditChange} placeholder={t('dogs:breed')} />
                                </div>
                                <div className="dog-info-tab__field-group">
                                    <label className="dog-info-tab__label">{t('dogs:dateOfBirth')}</label>
                                    <CustomDatePicker name="dateOfBirth" value={editForm.dateOfBirth} onChange={handleEditChange} max={new Date().toISOString().split('T')[0]} />
                                </div>
                                <div className="dog-info-tab__field-group">
                                    <label className="dog-info-tab__label">{t('dogs:gender')}</label>
                                    <div className="dog-info-tab__gender-selector">
                                        <span className={`dog-info-tab__gender-btn ${editForm.gender === "MALE" ? "dog-info-tab__gender-btn--selected" : ""}`} onClick={() => setEditForm(prev => ({ ...prev, gender: "MALE" }))} role="button" tabIndex="0">
                                            <IoMdMale size={20} /><span>{t('dogs:male')}</span>
                                        </span>
                                        <span className={`dog-info-tab__gender-btn ${editForm.gender === "FEMALE" ? "dog-info-tab__gender-btn--selected" : ""}`} onClick={() => setEditForm(prev => ({ ...prev, gender: "FEMALE" }))} role="button" tabIndex="0">
                                            <IoMdFemale size={20} /><span>{t('dogs:female')}</span>
                                        </span>
                                    </div>
                                </div>
                                <div className="dog-info-tab__checkbox-row">
                                    <label className="dog-info-tab__checkbox-label">
                                        <input type="checkbox" name="approximate" checked={editForm.approximate} onChange={handleEditChange} className="dog-info-tab__checkbox" />
                                        {t('dogs:approximateAge')}
                                    </label>
                                </div>
                                <div className="dog-info-tab__actions">
                                    <button className="dog-info-tab__save-btn" onClick={handleSave} disabled={saving}>
                                        {saving ? t('common:saving') : t('common:save')}
                                    </button>
                                    <button className="dog-info-tab__cancel-btn" onClick={cancelEditing} disabled={saving}>
                                        {t('common:cancel')}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="dog-info-tab__fields-grid">
                                    <div className="dog-info-tab__field-group">
                                        <label className="dog-info-tab__label">{t('dogs:name')}</label>
                                        <input className="dog-info-tab__input" type="text" value={dog.name || t('dogs:namePlaceholder')} disabled placeholder={t('dogs:namePlaceholder')} />
                                    </div>
                                    <div className="dog-info-tab__field-group">
                                        <label className="dog-info-tab__label">{t('dogs:breed')}</label>
                                        <input className="dog-info-tab__input" type="text" value={dog.breed || t('dogs:breedPlaceholder')} disabled placeholder={t('dogs:breedPlaceholder')} />
                                    </div>
                                    <div className="dog-info-tab__field-group">
                                        <label className="dog-info-tab__label">{t('dogs:age')}</label>
                                        <input className="dog-info-tab__input" type="text" value={dog.age || t('dogs:agePlaceholder')} disabled placeholder={t('dogs:agePlaceholder')} />
                                    </div>
                                    <div className="dog-info-tab__field-group">
                                        <label className="dog-info-tab__label">{t('dogs:gender')}</label>
                                        <input className="dog-info-tab__input" type="text" value={formatGender(dog.gender)} disabled placeholder={t('dogs:genderPlaceholder')} />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Owner Information Section */}
            {owner && (
                <div className="dog-info-tab__section dog-info-tab__owner-section">
                    <h3 className="dog-info-tab__section-title">{t('dogs:ownerInfo')}</h3>

                    <div className="dog-info-tab__row dog-info-tab__row--owner-info">
                        <div className="dog-info-tab__field-group">
                            <label className="dog-info-tab__label">{t('dogs:firstName')}</label>
                            <input className="dog-info-tab__input" type="text" value={owner.firstName || t('dogs:firstNamePlaceholder')} disabled placeholder={t('dogs:firstNamePlaceholder')} />
                        </div>
                        <div className="dog-info-tab__field-group">
                            <label className="dog-info-tab__label">{t('dogs:lastName')}</label>
                            <input className="dog-info-tab__input" type="text" value={owner.lastName || t('dogs:lastNamePlaceholder')} disabled placeholder={t('dogs:lastNamePlaceholder')} />
                        </div>
                        <div className="dog-info-tab__field-group">
                            <label className="dog-info-tab__label">{t('dogs:ownerEmail')}</label>
                            <input className="dog-info-tab__input" type="text" value={owner.email || t('dogs:ownerEmailPlaceholder')} disabled placeholder={t('dogs:ownerEmailPlaceholder')} />
                        </div>
                        <div className="dog-info-tab__field-group">
                            <label className="dog-info-tab__label">{t('dogs:phone')}</label>
                            <input className="dog-info-tab__input" type="text" value={owner.phone || t('dogs:phonePlaceholder')} disabled placeholder={t('dogs:phonePlaceholder')} />
                        </div>
                        <div className="dog-info-tab__field-group">
                            <label className="dog-info-tab__label">{t('dogs:address')}</label>
                            <input className="dog-info-tab__input" type="text" value={formatAddress(owner.address) || t('dogs:addressPlaceholder')} disabled placeholder={t('dogs:addressPlaceholder')} />
                        </div>
                    </div>
                </div>
            )}

            {/* Delete section for ownerless dogs */}
            {canEdit && !isEditing && (
                <div className="dog-info-tab__delete-section">
                    {!showDeleteConfirm ? (
                        <button
                            className="dog-info-tab__delete-btn"
                            onClick={() => setShowDeleteConfirm(true)}
                        >
                            {t('dogs:deletePupil')}
                        </button>
                    ) : (
                        <div className="dog-info-tab__delete-confirm">
                            <p className="dog-info-tab__delete-confirm-text">
                                {t('dogs:deleteConfirmText')}
                            </p>
                            <div className="dog-info-tab__delete-confirm-actions">
                                <button className="dog-info-tab__delete-confirm-btn" onClick={handleDelete} disabled={deleting}>
                                    {deleting ? t('dogs:deleting') : t('dogs:yesDelete')}
                                </button>
                                <button className="dog-info-tab__delete-cancel-btn" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
                                    {t('common:cancel')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default DogInfoTab;
